import { ApolloCache, ApolloLink, makeVar } from '@apollo/client';
import { getOperationAST } from 'graphql';
import { AD_CHANNELS, AdChannel } from 'types/adTypes';
import { getLocalAppSession } from './storageHelpers';
import { PENDING_CHANNEL_STORAGE_PREFIX } from './sessionCleanup';

type ChannelOperation = 'pending' | 'unknown' | 'failed';
export interface VacancyChannelIntent {
  customerId: number;
  channel: AdChannel;
  enabled: boolean;
  requestId: string;
  recorded?: boolean;
}
export interface VacancyChannelRecovery {
  channel: AdChannel;
  recorded: boolean;
  disabled: boolean;
}
const intentKey = (vacancyId: number, channel: AdChannel) =>
  `${vacancyId}:${channel}`;
const belongsToVacancy = (key: string, vacancyId: number) =>
  key.startsWith(`${vacancyId}:`);

const storagePrefix = () => {
  const session = getLocalAppSession();
  const user = session.userId ?? session.email;
  return user
    ? `${PENDING_CHANNEL_STORAGE_PREFIX}${encodeURIComponent(String(user))}:`
    : undefined;
};
const validIntent = (value: any): value is VacancyChannelIntent =>
  value &&
  Number.isInteger(value.customerId) &&
  value.customerId > 0 &&
  AD_CHANNELS.includes(value.channel) &&
  typeof value.enabled === 'boolean' &&
  typeof value.requestId === 'string' &&
  /^[0-9a-f-]{36}$/i.test(value.requestId) &&
  (value.recorded === undefined || typeof value.recorded === 'boolean');
const parseIntent = (raw: string | null): VacancyChannelIntent | null => {
  try {
    const value = JSON.parse(raw ?? 'null');
    return validIntent(value) ? value : null;
  } catch (_) {
    return null;
  }
};

/** Read both the original vacancy key and the new vacancy/channel keys. */
const readStoredIntents = (prefix: string) => {
  const result = new Map<string, VacancyChannelIntent>();
  const perChannel = new Set<string>();
  for (let index = 0; index < localStorage.length; index++) {
    const storageKey = localStorage.key(index);
    if (!storageKey?.startsWith(prefix)) continue;
    const parts = storageKey.slice(prefix.length).split(':');
    const vacancyId = Number(parts[0]);
    const intent = parseIntent(localStorage.getItem(storageKey));
    if (
      !Number.isInteger(vacancyId) ||
      vacancyId <= 0 ||
      String(vacancyId) !== parts[0] ||
      !intent ||
      parts.length > 2 ||
      (parts.length === 2 && parts[1] !== intent.channel)
    )
      continue;
    const key = intentKey(vacancyId, intent.channel);
    if (parts.length === 2) {
      result.set(key, intent);
      perChannel.add(key);
    } else if (!perChannel.has(key)) result.set(key, intent);
  }
  return result;
};
const createState = (prefix: string | undefined) => {
  let intents = new Map<string, VacancyChannelIntent>();
  if (prefix && typeof localStorage !== 'undefined') {
    try {
      intents = readStoredIntents(prefix);
    } catch (_) {
      /* A new request still requires a successful durable write. */
    }
  }
  return {
    prefix,
    order: 0,
    intents,
    selections: new Map<number, { order: number; channels: AdChannel[] }>(),
    operations: makeVar<ReadonlyMap<string, ChannelOperation>>(
      new Map(
        [...intents].map(([key, intent]) => [
          key,
          intent.recorded ? 'failed' : 'unknown',
        ])
      )
    ),
  };
};
const states = new WeakMap<object, ReturnType<typeof createState>>();
export const getVacancyChannelState = (cache: object) => {
  const prefix = storagePrefix();
  let state = states.get(cache);
  if (!state || state.prefix !== prefix) {
    state = createState(prefix);
    states.set(cache, state);
  }
  return state;
};
export const getVacancyChannelIntents = (cache: object, vacancyId: number) => {
  const state = getVacancyChannelState(cache);
  return [...state.intents]
    .filter(([key]) => belongsToVacancy(key, vacancyId))
    .map(([key, intent]) => ({
      intent,
      operation: state.operations().get(key),
    }));
};
export const setVacancyChannelOperation = (
  cache: object,
  vacancyId: number,
  channel: AdChannel,
  operation?: ChannelOperation
) => {
  const { operations } = getVacancyChannelState(cache);
  const next = new Map(operations());
  const key = intentKey(vacancyId, channel);
  if (operation) next.set(key, operation);
  else next.delete(key);
  operations(next);
};
const storedIntent = (
  prefix: string,
  vacancyId: number,
  channel: AdChannel
) => {
  const current = parseIntent(
    localStorage.getItem(prefix + intentKey(vacancyId, channel))
  );
  if (current?.channel === channel) return current;
  const legacy = parseIntent(localStorage.getItem(prefix + vacancyId));
  return legacy?.channel === channel ? legacy : null;
};
const persistIntent = (
  prefix: string,
  vacancyId: number,
  intent: VacancyChannelIntent
) => {
  localStorage.setItem(
    prefix + intentKey(vacancyId, intent.channel),
    JSON.stringify(intent)
  );
  const legacy = parseIntent(localStorage.getItem(prefix + vacancyId));
  if (
    legacy?.channel === intent.channel &&
    (legacy.recorded || legacy.requestId === intent.requestId)
  )
    localStorage.removeItem(prefix + vacancyId);
};

/** A known failure may be superseded only for the same channel. Other failures remain recoverable. */
export const beginVacancyChannelIntent = (
  cache: object,
  vacancyId: number,
  intent: VacancyChannelIntent
): boolean => {
  const state = getVacancyChannelState(cache);
  if (!state.prefix) throw new Error('The signed-in user is unavailable.');
  if (
    getVacancyChannelIntents(cache, vacancyId).some(
      ({ operation }) => operation === 'pending'
    )
  )
    return false;
  const next = new Map(state.operations());
  // Check storage again so an already-open tab also protects another tab's unconfirmed request.
  for (const [key, stored] of readStoredIntents(state.prefix)) {
    if (!belongsToVacancy(key, vacancyId)) continue;
    state.intents.set(key, stored);
    next.set(key, stored.recorded ? 'failed' : 'unknown');
  }
  const uncertain = [...state.intents].some(
    ([key, stored]) =>
      belongsToVacancy(key, vacancyId) &&
      !stored.recorded &&
      stored.requestId !== intent.requestId
  );
  if (uncertain) {
    state.operations(next);
    return false;
  }
  persistIntent(state.prefix, vacancyId, intent);
  const key = intentKey(vacancyId, intent.channel);
  state.intents.set(key, intent);
  next.set(key, 'pending');
  state.operations(next);
  return true;
};

export const acknowledgeVacancyChannelIntent = (
  cache: object,
  vacancyId: number,
  requestId: string
): boolean => {
  const state = getVacancyChannelState(cache);
  const entry = getVacancyChannelIntents(cache, vacancyId).find(
    ({ intent }) => intent.requestId === requestId
  );
  if (
    !state.prefix ||
    !entry ||
    storedIntent(state.prefix, vacancyId, entry.intent.channel)?.requestId !==
      requestId
  )
    return false;
  const recorded = { ...entry.intent, recorded: true };
  persistIntent(state.prefix, vacancyId, recorded);
  state.intents.set(intentKey(vacancyId, recorded.channel), recorded);
  setVacancyChannelOperation(cache, vacancyId, recorded.channel, 'failed');
  return true;
};
export const completeVacancyChannelIntent = (
  cache: object,
  vacancyId: number,
  requestId: string
) => {
  const state = getVacancyChannelState(cache);
  const entry = getVacancyChannelIntents(cache, vacancyId).find(
    ({ intent }) => intent.requestId === requestId
  );
  if (!entry) return;
  const key = intentKey(vacancyId, entry.intent.channel);
  try {
    if (state.prefix) {
      for (const storageKey of [state.prefix + key, state.prefix + vacancyId]) {
        if (
          parseIntent(localStorage.getItem(storageKey))?.requestId === requestId
        )
          localStorage.removeItem(storageKey);
      }
    }
  } catch (_) {
    /* A retained completed ID is safe to confirm again after a reload. */
  }
  state.intents.delete(key);
  setVacancyChannelOperation(cache, vacancyId, entry.intent.channel);
};

export const beginVacancyChannelWrite = (cache: object) =>
  ++getVacancyChannelState(cache).order;

export const confirmVacancyChannels = (
  cache: ApolloCache<any>,
  vacancyId: number,
  channels: AdChannel[],
  startedOrder?: number
): boolean => {
  const state = getVacancyChannelState(cache);
  const latest = state.selections.get(vacancyId);
  // A read during the write may contain either the old selection or a newer writer's result.
  // Reconcile with a read after this confirmed write instead of guessing which one it is.
  if (startedOrder !== undefined && latest && latest.order > startedOrder)
    return false;
  state.selections.set(vacancyId, {
    order: ++state.order,
    channels: [...channels],
  });
  cache.modify({
    id: cache.identify({ __typename: 'Vacancy', key: vacancyId }),
    fields: { channels: () => channels },
  });
  return true;
};

/** Protect selections from reads started before a newer confirmed result. */
export const createVacancyChannelsLink = (cache: object) =>
  new ApolloLink((operation, forward) => {
    if (
      getOperationAST(operation.query, operation.operationName)?.operation !==
      'query'
    )
      return forward(operation);
    const state = getVacancyChannelState(cache);
    const order = ++state.order;
    return forward(operation).map((result) => {
      if (!result.data) return result;
      let data = result.data;
      for (const [field, vacancy] of Object.entries(result.data)) {
        if (
          vacancy?.__typename !== 'Vacancy' ||
          typeof vacancy.key !== 'number' ||
          !Array.isArray(vacancy.channels)
        )
          continue;
        const latest = state.selections.get(vacancy.key);
        if (latest && latest.order > order)
          data = {
            ...data,
            [field]: { ...vacancy, channels: latest.channels },
          };
        else
          state.selections.set(vacancy.key, {
            order,
            channels: [...vacancy.channels],
          });
      }
      return data === result.data ? result : { ...result, data };
    });
  });
